/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-12-18
 */

// Add third-party dependencies.
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Divider,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stack,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';

// Add local dependencies.
import {
  useGetPublicChecklistQuery,
  useSubmitParentResponsesMutation,
} from '../../api/classEssentialsApi';

/**
 * Parent Checklist View page component.
 * Public page for parents to view and respond to class essentials checklist.
 *
 * @returns {React.ReactElement} The parent checklist view component
 */
const ParentChecklistView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Extract email from URL query parameter
  const searchParams = new URLSearchParams(window.location.search);
  const emailFromUrl = searchParams.get('email') || '';

  const {
    data: message,
    isLoading,
    error,
  } = useGetPublicChecklistQuery(token || '');

  // Extract recipient data from the message response
  const recipientEmail = message?.recipient_email || emailFromUrl;
  const parentName = message?.recipient_parent_name || '';
  const studentName = message?.recipient_student_name || '';

  const [submitResponses, { isLoading: isSubmitting }] =
    useSubmitParentResponsesMutation();

  const handleResponseChange = (itemId: string, value: string) => {
    setResponses(prev => ({
      ...prev,
      [itemId]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!token || !message) {
      return;
    }

    try {
      setSubmitError(null);

      // Build responses array from the responses state
      const responsesArray = Object.entries(responses).map(
        ([itemId, status]) => ({
          item_selection_id: itemId,
          status,
          notes: '',
        })
      );

      // Submit to backend
      await submitResponses({
        token,
        responses: responsesArray,
        recipient_email: recipientEmail || undefined,
        parent_name: parentName || undefined,
        student_name: studentName || undefined,
      }).unwrap();

      setSubmitted(true);
    } catch (err: any) {
      setSubmitError(
        err?.data?.error ||
          err?.data?.detail ||
          err?.message ||
          'Failed to submit your selections. Please try again.'
      );
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !message) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error">
          This checklist link is invalid or has expired. Please contact the
          studio for a new link.
        </Alert>
      </Container>
    );
  }

  if (submitted) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleIcon
            sx={{ fontSize: 80, color: 'success.main', mb: 2 }}
          />
          <Typography variant="h4" gutterBottom>
            Thank You!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Your selections have been submitted to the studio. They will contact
            you regarding the items you need.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            {message.title || 'Class Essentials Checklist'}
          </Typography>
          {message.notes && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {message.notes}
            </Alert>
          )}
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* Show recipient info if available */}
        {(parentName || studentName) && (
          <Box sx={{ mb: 4 }}>
            <Alert severity="info">
              {parentName && (
                <div>
                  <strong>Parent/Guardian:</strong> {parentName}
                </div>
              )}
              {studentName && (
                <div>
                  <strong>Student:</strong> {studentName}
                </div>
              )}
            </Alert>
          </Box>
        )}

        {/* Instructions */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1" paragraph>
            Please review the items below and let us know what you need. Select
            one option for each item:
          </Typography>
          <Typography variant="body2" color="text.secondary" component="ul">
            <li>
              <strong>Already have:</strong> You already own this item
            </li>
            <li>
              <strong>Want to purchase:</strong> You need to get this item and
              prefer to purchase it yourself
            </li>
            <li>
              <strong>Purchase from studio:</strong> You need this item and want
              to purchase it from the studio (pick up in-person)
            </li>
          </Typography>
        </Box>

        {/* Items List */}
        <Stack spacing={3}>
          {message.items && message.items.length > 0 ? (
            message.items.map(item => (
              <Paper key={item.id} variant="outlined" sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                    flexWrap: { xs: 'wrap', md: 'nowrap' },
                  }}
                >
                  {/* Item Details */}
                  <Box
                    sx={{
                      flex: '0 0 auto',
                      minWidth: { xs: '100%', md: '200px' },
                    }}
                  >
                    <Typography variant="h6" gutterBottom>
                      {item.master_item_name || 'Item'}
                    </Typography>

                    {item.master_item_price !== null &&
                      item.master_item_price !== undefined && (
                        <Typography variant="body2" color="primary">
                          ${Number(item.master_item_price).toFixed(2)}
                        </Typography>
                      )}

                    {item.master_item_description && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 0.5 }}
                      >
                        {item.master_item_description}
                      </Typography>
                    )}
                  </Box>

                  {/* Radio Buttons */}
                  <FormControl component="fieldset" sx={{ flex: 1 }}>
                    <RadioGroup
                      row
                      value={responses[item.id] || ''}
                      onChange={e =>
                        handleResponseChange(item.id, e.target.value)
                      }
                    >
                      <FormControlLabel
                        value="already_have"
                        control={<Radio />}
                        label="Already have"
                      />
                      <FormControlLabel
                        value="want_to_purchase"
                        control={<Radio />}
                        label="Want to purchase"
                      />
                      <FormControlLabel
                        value="purchase_in_person"
                        control={<Radio />}
                        label="Purchase from studio"
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Paper>
            ))
          ) : (
            <Alert severity="info">
              No items in this checklist. Please contact the studio.
            </Alert>
          )}
        </Stack>

        {/* Error Display */}
        {submitError && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {submitError}
          </Alert>
        )}

        {/* Submit Button */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={Object.keys(responses).length === 0 || isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit My Selections'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ParentChecklistView;
